import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import BenchmarkToggle from '../../components/BenchmarkToggle/BenchmarkToggle';
import Card from '../../components/Card/Card';
import ChartWrapper from '../../components/ChartWrapper/ChartWrapper';
import ErrorMessageNewFund from '../../components/ErrorMessages/ErrorMessageNewFund';
import ErrorMessageNoData from '../../components/ErrorMessages/ErrorMessageNoData';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import LineChart from '../../components/LineChart/LineChart';
import TablePreRender from '../../components/Table/Table.prerender';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import ChartTooltip from '../../components/ChartTooltip/ChartTooltip';
import ViewDataTable from '../../components/ViewDataTable/ViewDataTable';

import footers from './PerformanceChart.footers';

import styles from './PerformanceChart.css';

import { performanceChartCardUtil } from './util';
import { displayCurrency } from '../../utils/Format/Format';
import { CARD_NAMES } from '../../utils/constants';

import { PERFORMANCE_QUERY, ETF_PERFORMANCE_QUERY } from './queries';
import { isExpectingFullYearData } from '../../utils/time';

const twentyFiveK = ['PRINX', 'PRSMX', 'TRSXX'];

class PerformanceChartCard extends React.Component {
    state = {
        toggledOff: []
    };

    onLegendToggle = (v, e) => {
        const { toggledOff } = this.state;
        const legendLength =
            e.target.parentNode.parentNode.parentNode.childElementCount;

        // Don't allow all legend elements to be toggled off
        if (toggledOff.length === legendLength - 1 && !toggledOff.includes(v)) {
            e.preventDefault();
        } else {
            this.setState((prevState) => {
                const newToggleState = !prevState.toggledOff.includes(v)
                    ? prevState.toggledOff.concat([v])
                    : prevState.toggledOff.filter((ele) => ele !== v);
                return { toggledOff: newToggleState };
            });
        }
    };

    render() {
        const { etf, ticker } = this.props;
        const { toggledOff } = this.state;
        return (
            <GqlQueryWrapper
                filter={toggledOff}
                query={etf ? ETF_PERFORMANCE_QUERY : PERFORMANCE_QUERY}
                variables={{ ticker }}
                dataFunc={performanceChartCardUtil}
                placeholder={() => <TablePreRender />}
            >
                {({
                    data: {
                        asOf,
                        colors,
                        data,
                        inceptionDate,
                        legendData,
                        ytdMonthlyAsOfDate,
                        benchmarkName,
                        canShow
                    }
                }) => {
                    // data = null;
                    // inceptionDate = "2022-07-31T04:00:00Z";

                    if (!canShow) {
                        return (
                            <Card>
                                {isExpectingFullYearData(inceptionDate) ? (
                                    <ErrorMessageNoData
                                        cardName={CARD_NAMES.PERFORMANCE_CHART}
                                    />
                                ) : (
                                    <ErrorMessageNewFund
                                        cardName={CARD_NAMES.PERFORMANCE_CHART}
                                    />
                                )}
                            </Card>
                        );
                    }

                    return (
                        <React.Fragment>
                            <Card>
                                <div className="container">
                                    <TitleWithDescription
                                        title={`Cumulative Returns of Hypothetical $${
                                            twentyFiveK.includes(ticker)
                                                ? `25,000`
                                                : `10,000`
                                        } ${etf ? '(NAV)' : ''} as of ${asOf}`}
                                    />
                                    <ChartWrapper
                                        tipContent={(d) => (
                                            <ChartTooltip
                                                data={d}
                                                format={(val) =>
                                                    displayCurrency(val)
                                                }
                                            />
                                        )}
                                    >
                                        {({
                                            marginLeft,
                                            width,
                                            toggleTooltip,
                                            updateTooltip
                                        }) => (
                                            <LineChart
                                                colors={colors}
                                                data={data}
                                                width={width}
                                                height={300}
                                                marginBottom={30}
                                                marginLeft={marginLeft}
                                                toggleTooltip={toggleTooltip}
                                                updateTooltip={updateTooltip}
                                                accountForDuplicateData
                                            />
                                        )}
                                    </ChartWrapper>
                                    <BenchmarkToggle
                                        data={legendData}
                                        onToggle={this.onLegendToggle}
                                    />
                                    {['PRINX', 'PRSMX', 'TRSXX'].includes(
                                        ticker
                                    ) ? (
                                        <React.Fragment>
                                            <div className="disclaimer">
                                                This chart shows the value of a
                                                hypothetical $25,000 investment
                                                in the fund over the past 10
                                                years or since its inception
                                                (for funds lacking 10-year
                                                records) compared with its{' '}
                                                <span id="benchmarkName">
                                                    {' '}
                                                    benchmark
                                                </span>. Lipper averages consist
                                                of all the mutual funds in a
                                                particular category; Lipper
                                                indexes consist of a small
                                                number (10 or 30) of the largest
                                                funds in a category.
                                            </div>
                                            <div className="disclaimer">
                                                *Summit funds require a $25,000
                                                Minimum Investment.
                                            </div>
                                        </React.Fragment>
                                    ) : (
                                        <div className="disclaimer">
                                            This chart shows the value of a
                                            hypothetical $10,000 investment in
                                            the fund over the past 10 years or
                                            since its inception (for funds
                                            lacking 10-year records). The result
                                            is compared with benchmarks, which
                                            may include a broad-based market
                                            index and a peer group average or
                                            index. Market indexes do not include
                                            expenses, which are deducted from
                                            fund returns as well as mutual fund
                                            averages and indexes.
                                        </div>
                                    )}
                                    {benchmarkName
                                        .toLowerCase()
                                        .includes('combined') && (
                                        <div className="disclaimer">
                                            {ReactHtmlParser(
                                                footers('').BLENDED_PORTFOLIO
                                            )}
                                        </div>
                                    )}
                                    {/* Hard Coded to match JSP Logic */}
                                    {[
                                        'POMIX',
                                        'PEXMX',
                                        'PRSIX',
                                        'TRPBX',
                                        'TRLAX',
                                        'TRSGX'
                                    ].includes(ticker) && (
                                        <div className="disclaimer">
                                            {ReactHtmlParser(
                                                footers(ytdMonthlyAsOfDate)[
                                                    ticker
                                                ]
                                            )}
                                        </div>
                                    )}
                                </div>

                                <style jsx>{styles}</style>
                            </Card>

                            <ViewDataTable
                                modalId="performance-chart-view-data"
                                audioLabel="View Data for Cumulative Returns of Hypothetical $10,000 Investment in a table format"
                            >
                                <table
                                    className="view-data-table"
                                    title="Cumulative Returns of Hypothetical $10,000 Data Table"
                                >
                                    <caption>
                                        Data for Cumulative Returns of
                                        Hypothetical $10,000 Investment
                                    </caption>
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            {/* each data point has a copy of the headers, using the first data point to get a copy */}
                                            {data[0].headers.map((header) => (
                                                <th
                                                    key={header}
                                                >{`${header} Value`}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map(
                                            ({ label, data: rowData }) => (
                                                <tr key={label}>
                                                    <td
                                                        style={{
                                                            textAlign: 'right'
                                                        }}
                                                    >
                                                        {label}
                                                    </td>
                                                    {rowData.map(
                                                        (value, index) => (
                                                            // it's bad practice to use index as a key but because
                                                            // it would be ADA non-compliant to have ui elments that
                                                            // shuffle these table cells around on the page anyway,
                                                            // and the value is not garaunteed unique, I'm making a
                                                            // very rare exception this time
                                                            <td
                                                                /* eslint-disable-next-line */
                                                                key={index}
                                                                style={{
                                                                    textAlign:
                                                                        'right'
                                                                }}
                                                            >
                                                                {displayCurrency(
                                                                    value
                                                                )}
                                                            </td>
                                                        )
                                                    )}
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </ViewDataTable>
                        </React.Fragment>
                    );
                }}
            </GqlQueryWrapper>
        );
    }
}

export default PerformanceChartCard;
